import { NoSsr } from "@mui/base";
import { useTheme } from "@mui/material/styles";
import { theme, useResponsive } from "@product/scmp-sdk";
import { usePrevious } from "@react-hookz/web";
import { useScroll } from "ahooks";
import merge from "deepmerge";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";

import { appBarScrollStickyAtom } from "~/components/app-bar/hooks/sticky/atoms";
import { HomeTypeformBackToOriginalScmpButton } from "~/components/app-bar/typeform-button";
import { HeaderBottomContainerElementID } from "~/pages/_app/consts";

import { appBarAdSlotAtom, appBarAtom } from "./atoms";
import { DynamicAdSlot } from "./dynamics";
import type { AppBarVariant } from "./helpers";
import { useAppBarSetStickyHeight } from "./hooks";
import {
  AdSlotContainer,
  Container,
  FullWidthBackground,
  HeaderBottomContainer,
  StyledHeader,
  StyledHomeHeader,
  StyledPostiesBrochureHeader,
  StyledPostiesContentHeader,
  StyledStyleContentHeader,
} from "./styles";
import type { AppBarConfiguration } from "./types";

export type Props = {
  children?: ReactNode;
  className?: string;
  hideTargetChildren?: ReactNode;
  pageProps?: PageProps;
};

export const AppBar: FunctionComponent<Props> = ({ children, className, pageProps }) => {
  const { adSlotHeight, bindAdSlotContainer, responsiveAdSlot, stickyStyle } = useResponsiveAdSlot(
    pageProps?.appBarConfiguration,
  );

  const appBarValue = useAtomValue(appBarAtom);
  const { stickyScrollHeight = 0 } = useAtomValue(appBarScrollStickyAtom);
  const appBarStickyScrollHeight = useMemo(
    () => stickyScrollHeight + adSlotHeight,
    [adSlotHeight, stickyScrollHeight],
  );

  const appBarVariant = useMemo<AppBarVariant>(
    () =>
      appBarValue?.scrollOverVariant ?? pageProps?.appBarConfiguration?.variant ?? "genericLight",
    [appBarValue?.scrollOverVariant, pageProps?.appBarConfiguration?.variant],
  );

  const hideElementOnScrollEffect = useStickyWithHideElementAfterScrollThreshold(
    200,
    appBarStickyScrollHeight,
  );
  const stickyWithScrollAwayElement = useStickyWithScrollAwayElement(appBarStickyScrollHeight);

  const styles: CSSProperties = {
    ...(stickyStyle === "withHideElementAfterScrollThreshold"
      ? hideElementOnScrollEffect.targetContainerStyles
      : stickyWithScrollAwayElement.targetContainerStyles),
  };

  const { reference } = useAppBarSetStickyHeight();

  const renderHeader = () => {
    switch (appBarVariant) {
      case "posties":
        return <StyledPostiesContentHeader />;
      case "postiesBrochure":
        return <StyledPostiesBrochureHeader />;
      case "magazines-style":
        const headerConfiguration = {
          ...pageProps?.headerConfiguration,
          appBarStickyScrollHeight,
          appBarVariant,
        };
        return <StyledStyleContentHeader headerConfiguration={headerConfiguration} />;
      case "home":
        const homeHeaderConfiguration = {
          ...pageProps?.headerConfiguration,
          appBarStickyScrollHeight,
          appBarVariant,
        };
        return <StyledHomeHeader headerConfiguration={homeHeaderConfiguration} />;
      default:
        return <StyledHeader headerConfiguration={pageProps?.headerConfiguration} />;
    }
  };

  return (
    <Container
      $appBarVariant={appBarVariant}
      className={className}
      ref={reference}
      style={{ ...styles }}
    >
      <FullWidthBackground />
      <AdSlotContainer {...bindAdSlotContainer}>
        <NoSsr>{responsiveAdSlot}</NoSsr>
      </AdSlotContainer>
      <HomeTypeformBackToOriginalScmpButton />
      {children}
      {renderHeader()}
      <HeaderBottomContainer id={HeaderBottomContainerElementID} />
    </Container>
  );
};

AppBar.displayName = "AppBar";

const defaultAdSlotVariants = {
  large: {
    height: 250,
    padding: 20,
  },
  small: {
    height: 100,
    padding: 10,
  },
} as const;

const styleAdSlotVariants = merge(defaultAdSlotVariants, {
  small: {
    padding: 20,
  },
});

const useResponsiveAdSlot = (configuration?: AppBarConfiguration) => {
  const adSlotValue = useAtomValue(appBarAdSlotAtom);

  const adSlotVariants = {
    ...(configuration?.variant === "magazines-style" ? styleAdSlotVariants : defaultAdSlotVariants),
    ...configuration?.adSlotVariants,
  };

  const bindAdSlotContainer = useMemo(() => {
    const largeStyles = {
      $height: adSlotVariants.large.height,
      $isVisible: configuration?.hasDesktopAd,
      $padding: adSlotVariants.large.padding,
    };
    const smallStyles = {
      $height: adSlotVariants.small.height,
      $isVisible: configuration?.hasMobileAd,
      $padding: adSlotVariants.small.padding,
    };

    switch (configuration?.variant) {
      case "home":
        return {
          $responsiveVariants: {
            desktopUp: smallStyles,
            homeDesktopUp: largeStyles,
            mobileUp: smallStyles,
            tabletUp: smallStyles,
          },
        };
      default:
        return {
          $responsiveVariants: {
            desktopUp: largeStyles,
            mobileUp: smallStyles,
            tabletUp: smallStyles,
          },
        };
    }
  }, [
    adSlotVariants.large.height,
    adSlotVariants.large.padding,
    adSlotVariants.small.height,
    adSlotVariants.small.padding,
    configuration?.hasDesktopAd,
    configuration?.hasMobileAd,
    configuration?.variant,
  ]);

  const isDesktopUp = useResponsive(
    theme.breakpoints.up(configuration?.desktopBreakpoint ?? "desktop"),
  );
  const { adSlotHeight, responsiveAdSlot, stickyStyle } = useMemo(() => {
    switch (true) {
      case isDesktopUp:
        return {
          adSlotHeight: adSlotValue?.desktop
            ? adSlotVariants.large.height + adSlotVariants.large.padding * 2
            : 0,
          responsiveAdSlot: adSlotValue?.desktop ? (
            <DynamicAdSlot
              sizes={[
                [970, 250],
                [728, 90],
                [970, 90],
              ]}
              {...adSlotValue.desktop}
            />
          ) : null,
          stickyStyle: "withScrollAwayElement" as const,
        };
      default:
        return {
          adSlotHeight: adSlotValue?.mobile
            ? adSlotVariants.small.height + adSlotVariants.small.padding * 2
            : 0,
          responsiveAdSlot: adSlotValue?.mobile ? (
            <DynamicAdSlot
              sizes={[
                [300, 100],
                [320, 100],
                [300, 50],
                [320, 50],
              ]}
              {...adSlotValue.mobile}
            />
          ) : null,
          stickyStyle: "withHideElementAfterScrollThreshold" as const,
        };
    }
  }, [
    adSlotValue?.desktop,
    adSlotValue?.mobile,
    adSlotVariants.large.height,
    adSlotVariants.large.padding,
    adSlotVariants.small.height,
    adSlotVariants.small.padding,
    isDesktopUp,
  ]);

  return {
    adSlotHeight,
    bindAdSlotContainer,
    responsiveAdSlot,
    stickyStyle,
  };
};

// This effect will hide the element with transition after the scroll container scroll passes a threshold
const useStickyWithHideElementAfterScrollThreshold = (
  scrollThreshold: number,
  targetElementHeight: number,
) => {
  const theme = useTheme();
  const position = useScroll();
  const containerElementScrollOffsetY = position?.top ?? 0;
  const previousTargetElementHeight = usePrevious(targetElementHeight);

  const shouldHidden = containerElementScrollOffsetY > scrollThreshold;
  const shouldPauseTransition = previousTargetElementHeight !== targetElementHeight;

  const targetContainerStyles: CSSProperties = {
    position: "sticky",
    transform: shouldHidden ? `translateY(-${targetElementHeight}px)` : "none",
    transition: shouldPauseTransition
      ? "none" // eslint-disable-next-line etc/no-internal
      : theme.transitions.create("transform", {
          duration: theme.transitions.duration.standard,
        }),
  };

  return {
    targetContainerStyles,
  };
};

// This effect will make the container sticky after the scroll away element is complete scroll
// passed in the scroll container
const useStickyWithScrollAwayElement = (scrollAwayElementHeight: number) => {
  const position = useScroll();
  const containerElementScrollOffsetY = position?.top ?? 0;

  const shouldStick =
    containerElementScrollOffsetY > 0 && containerElementScrollOffsetY >= scrollAwayElementHeight;

  const targetContainerStyles: CSSProperties = {
    position: shouldStick ? "sticky" : "relative",
    transform: shouldStick ? `translateY(-${scrollAwayElementHeight}px)` : "none",
  };

  return {
    targetContainerStyles,
  };
};
